import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft, Minus, Plus, ShoppingCart, Check } from "lucide-react";
import { toast } from "sonner";
import { FadeIn, MaskedLine, MonoLabel } from "../components/motion";
import { useCart } from "../context/CartContext";
import { api } from "../lib/api";

const ProductDetail = () => {
    const { slug } = useParams();
    const { addItem } = useCart();
    const [product, setProduct] = useState(null);
    const [notFound, setNotFound] = useState(false);
    const [qty, setQty] = useState(1);

    useEffect(() => {
        setProduct(null);
        setNotFound(false);
        setQty(1);
        api.get(`/products/${slug}`)
            .then((r) => setProduct(r.data))
            .catch(() => setNotFound(true));
    }, [slug]);

    if (notFound) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-8 px-6 text-center" data-testid="product-not-found">
                <p className="font-heading font-black uppercase tracking-tighter text-4xl">Product not found</p>
                <Link to="/products" className="px-10 h-14 flex items-center bg-[#FF5C00] text-white font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300">
                    Back to Products
                </Link>
            </div>
        );
    }

    if (!product) {
        return (
            <div className="min-h-screen pt-32 px-4 md:px-8" data-testid="product-loading">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-px bg-line border border-line">
                    <div className="aspect-square bg-charcoal animate-pulse" />
                    <div className="p-10 space-y-4 bg-[#050505]">
                        <div className="h-6 bg-charcoal animate-pulse w-1/2" />
                        <div className="h-4 bg-charcoal animate-pulse w-full" />
                        <div className="h-4 bg-charcoal animate-pulse w-2/3" />
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="pt-24 md:pt-28" data-testid="product-detail-page">
            <div className="px-4 md:px-8 pb-6">
                <Link to="/products" data-testid="product-back-link" className="group inline-flex items-center gap-3 font-mono text-xs tracking-[0.2em] uppercase text-white/50 hover:text-[#FF5C00] transition-colors duration-300">
                    <ArrowLeft className="w-4 h-4 transition-transform duration-300 group-hover:-translate-x-1" /> Back to Products
                </Link>
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 border-y border-line">
                <div className="relative bg-white border-b lg:border-b-0 lg:border-r border-line overflow-hidden group min-h-[320px]">
                    <img
                        src={product.image}
                        alt={product.name}
                        data-testid="product-image"
                        className="absolute inset-0 w-full h-full object-cover grayscale group-hover:grayscale-0 group-hover:scale-105 transition-[transform,filter] duration-700"
                    />
                    {product.image_credit && (
                        <span className="absolute bottom-3 right-3 font-mono text-[9px] tracking-wider text-white/50 bg-black/60 px-2 py-1" data-testid="product-image-credit">
                            {product.image_credit}
                        </span>
                    )}
                    <span className="absolute top-4 left-4 font-mono text-[10px] tracking-[0.2em] uppercase bg-black text-white px-3 py-1">
                        {product.category.replace("-", " ")}
                    </span>
                </div>

                <div className="p-8 md:p-12 lg:p-16">
                    <MonoLabel>{product.category.replace("-", " ")}</MonoLabel>
                    <h1 className="mt-4 font-heading font-black uppercase tracking-tighter leading-[0.95] text-3xl md:text-5xl" data-testid="product-name">
                        <MaskedLine>{product.name}</MaskedLine>
                    </h1>
                    <FadeIn delay={0.15}>
                        <p className="mt-6 text-white/70 leading-relaxed" data-testid="product-description">{product.description}</p>
                    </FadeIn>

                    <FadeIn delay={0.2} className="mt-8">
                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-3">Applications</p>
                        <div className="flex flex-wrap gap-2" data-testid="product-applications">
                            {product.applications.map((a) => (
                                <span key={a} className="border border-line px-3 py-1.5 font-mono text-[10px] tracking-wider uppercase text-white/70">{a}</span>
                            ))}
                        </div>
                    </FadeIn>

                    <FadeIn delay={0.25} className="mt-8">
                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40 mb-3">Key Benefits</p>
                        <ul className="space-y-2" data-testid="product-benefits">
                            {product.benefits.map((b) => (
                                <li key={b} className="flex items-center gap-3 text-sm text-white/70">
                                    <Check className="w-4 h-4 text-[#FF5C00] shrink-0" /> {b}
                                </li>
                            ))}
                        </ul>
                    </FadeIn>

                    <FadeIn delay={0.3} className="mt-8">
                        <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Size</p>
                        <p className="mt-3 font-mono text-sm text-white/70" data-testid="product-size">{product.size || "Supplied in standard size"}</p>
                    </FadeIn>

                    {product.equivalent && (
                        <FadeIn delay={0.32} className="mt-8 border border-line p-5" >
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">Equivalent Grade — Strength Class</p>
                            <p className="mt-2 font-mono text-sm text-[#FF5C00]" data-testid="product-equivalent">{product.equivalent}</p>
                        </FadeIn>
                    )}

                    <FadeIn delay={0.35} className="mt-10 flex flex-wrap items-center gap-6">
                        <div className="flex items-center border border-line">
                            <button data-testid="detail-qty-decrease" onClick={() => setQty((q) => Math.max(1, q - 1))} className="w-12 h-12 flex items-center justify-center hover:text-[#FF5C00] transition-colors" aria-label="Decrease quantity">
                                <Minus className="w-4 h-4" />
                            </button>
                            <span className="w-12 text-center font-mono" data-testid="detail-qty-value">{qty}</span>
                            <button data-testid="detail-qty-increase" onClick={() => setQty((q) => q + 1)} className="w-12 h-12 flex items-center justify-center hover:text-[#FF5C00] transition-colors" aria-label="Increase quantity">
                                <Plus className="w-4 h-4" />
                            </button>
                        </div>
                        <span className="font-mono text-sm text-white/70" data-testid="detail-price">
                            {product.price ? `$${product.price}` : "Request Pricing"}
                        </span>
                    </FadeIn>

                    <FadeIn delay={0.4} className="mt-8 flex flex-col sm:flex-row gap-px bg-line border border-line">
                        <button
                            data-testid="detail-add-to-cart"
                            onClick={() => { addItem(product, qty); toast.success("Added to order enquiry"); }}
                            className="flex-1 flex items-center justify-center gap-3 h-14 bg-[#FF5C00] text-white font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            <ShoppingCart className="w-4 h-4" /> Add to Order
                        </button>
                        <Link
                            to={`/contact?product=${encodeURIComponent(product.name)}`}
                            data-testid="detail-enquire-link"
                            className="flex-1 flex items-center justify-center gap-3 h-14 bg-[#050505] text-white font-mono text-xs tracking-[0.2em] uppercase hover:bg-white hover:text-black transition-colors duration-300"
                        >
                            Enquire About This Product
                        </Link>
                    </FadeIn>
                </div>
            </div>

            <section className="border-b border-line" data-testid="product-specs-section">
                <div className="px-8 md:px-16 py-12">
                    <MonoLabel>Technical Specifications</MonoLabel>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 border-t border-line">
                    {product.specs.map((s) => (
                        <div key={s.label} className="p-8 border-r border-b xl:border-b-0 border-line" data-testid={`spec-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                            <p className="font-mono text-[10px] tracking-[0.25em] uppercase text-white/40">{s.label}</p>
                            <p className="mt-3 font-mono text-lg text-white">{s.value}</p>
                            {s.standard && <p className="mt-1 font-mono text-[10px] tracking-wider uppercase text-white/30">{s.standard}</p>}
                        </div>
                    ))}
                </div>
                <p className="px-8 md:px-16 py-6 font-mono text-[10px] tracking-[0.2em] uppercase text-white/30 border-t border-line">
                    {product.specs_note || "Placeholder data — full specifications supplied on enquiry."}
                </p>
                {(product.datasheets || (product.datasheet ? [product.datasheet] : [])).length > 0 && (
                    <div className="border-t border-line" data-testid="datasheet-section">
                        <div className="px-8 md:px-16 pt-12 pb-6">
                            <MonoLabel>Spec Sheet — Read Online</MonoLabel>
                        </div>
                        <div className="bg-white px-4 md:px-16 pb-16 pt-2 flex flex-col items-center gap-8">
                            {(product.datasheets || [product.datasheet]).map((src, idx) => (
                                <img
                                    key={src}
                                    src={src}
                                    alt={`${product.name} — specification sheet ${idx + 1}`}
                                    data-testid={`datasheet-image-${idx + 1}`}
                                    className="w-full max-w-4xl h-auto"
                                    loading="lazy"
                                />
                            ))}
                        </div>
                    </div>
                )}
            </section>
        </div>
    );
};

export default ProductDetail;
